import * as React from "react"
import { useSearchParams } from "react-router"

import { deserializeFilters, serializeFilters } from "./filter-serialize"
import { activeFilterCount, type FilterDef, type FilterValues } from "./filter-types"
import type { TableSort } from "./use-table"

/*
   A table that keeps its whole query in the URL. Users, Clients and the Queue all use it, so
   they behave the same way:

     /admin/users?q=alex&role=reviewer&activity=never&page=2&size=25&sort=email&dir=asc
     /admin/clients?q=acme&status=inactive&hasPrimary=missing&sort=name&dir=asc
     /reviews?q=xsate&status=needs_sme&findings=outstanding&unread=1&page=2&size=25

   The URL is the single source of truth, so a filtered view can be shared or bookmarked,
   the back button steps through what the reader did, and a sidebar link to bare
   /admin/users really does reset the view. Defaults are left out of the URL rather than
   written into it, so the plain page keeps a plain address.

   Every write starts from the newest params (see `update`), never from a render's closure.
   The search box debounces, so its timer fires holding whichever closure existed when the
   keystroke landed; building the URL from that closure would silently drop a filter set
   inside the debounce window. The pager writes twice in one tick, which a closure would
   collapse into one.
*/

const PARAM = { search: "q", page: "page", size: "size", sort: "sort", dir: "dir" } as const

export const TABLE_PAGE_SIZES: readonly number[] = [10, 25, 50]
const DEFAULT_PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 300

export interface TableUrlState {
  /** What the search box shows — updates on every keystroke. */
  search: string
  /** The debounced search actually in the URL, and the one the query sends. */
  committedSearch: string
  page: number
  pageSize: number
  sort: TableSort
  filters: FilterValues
  filterCount: number
  /** Filters whose URL values were dropped, wholly or partly, as options the panel lacks. */
  droppedFilterIds: readonly string[]
}

export interface TableUrlActions {
  setSearch: (search: string) => void
  /**
   * Drops `q` from the URL in this tick rather than on the debounce. A "Clear filters"
   * control needs this: `setSearch("")` only empties the draft, so the URL keeps the old
   * `q` for another 300 ms, and the list goes on fetching a search the reader has just
   * cleared — with the empty state still claiming the view is filtered.
   */
  clearSearch: () => void
  /** `replace`: for a page the reader did not choose — see the correction case below. */
  setPage: (page: number, options?: { replace?: boolean }) => void
  setPageSize: (size: number) => void
  setSort: (sort: TableSort | null) => void
  setFilters: (values: FilterValues) => void
  clearFilter: (id: string) => void
  clearAllFilters: () => void
}

export function useTableUrlState({
  schema,
  sortFields,
  defaultSort,
  pageSizes = TABLE_PAGE_SIZES,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  normalizeFilters,
  clearSearchOnClearAll = false,
}: {
  schema: FilterDef[]
  /** Sort fields the backend accepts. Anything else in the URL falls back to the default. */
  sortFields: readonly string[]
  defaultSort: TableSort
  /** Sizes the pager offers. A `size` outside them falls back to `defaultPageSize`. */
  pageSizes?: readonly number[]
  defaultPageSize?: number
  /**
   * Rewrites the URL's filters before they are checked against the schema's options, for a
   * value that is valid but spelled differently from its option (the viewer's own id read as
   * "Me"). Keep it stable (`useCallback`): it is a memo dependency.
   */
  normalizeFilters?: (values: FilterValues) => FilterValues
  /**
   * Whether "clear all" also clears the search. Off for the admin tables, where Clear all
   * means the filter chips; on for the Queue, where Clear resets the whole query (legacy).
   */
  clearSearchOnClearAll?: boolean
}): { state: TableUrlState; actions: TableUrlActions } {
  const [params, setParams] = useSearchParams()

  const committedSearch = params.get(PARAM.search) ?? ""
  const page = parsePositiveInt(params.get(PARAM.page)) ?? 1
  const rawSize = parsePositiveInt(params.get(PARAM.size))
  const pageSize = rawSize !== null && pageSizes.includes(rawSize) ? rawSize : defaultPageSize
  const sort = parseSort(params, sortFields) ?? defaultSort
  const paramsKey = params.toString()
  const { values: filters, dropped: droppedFilterIds } = React.useMemo(() => {
    const parsed = deserializeFilters(new URLSearchParams(paramsKey), schema)
    return knownOptionsOnly(normalizeFilters ? normalizeFilters(parsed) : parsed, schema)
  }, [paramsKey, schema, normalizeFilters])

  // `setSearchParams((prev) => …)` does not chain: `prev` is the URL as of the last render,
  // so two writes in the same tick both start from it and the second erases the first. The
  // pager does exactly that — a page-size change is followed at once by a page change that
  // keeps the reader on the same rows. So writes start from the newest params we know of:
  // the one still waiting to render if there is one, otherwise the rendered URL.
  const latestParams = React.useRef(params)
  const pendingParams = React.useRef<URLSearchParams | null>(null)
  latestParams.current = params
  React.useEffect(() => {
    // The URL has rendered; whatever was pending is now in it.
    pendingParams.current = null
  }, [params])

  /** Apply `mutate` to the newest params. Any change except paging goes back to page 1. */
  const update = React.useCallback(
    (
      mutate: (next: URLSearchParams) => void,
      { keepPage = false, replace = false }: { keepPage?: boolean; replace?: boolean } = {},
    ) => {
      const next = new URLSearchParams(pendingParams.current ?? latestParams.current)
      mutate(next)
      if (!keepPage) next.delete(PARAM.page)
      pendingParams.current = next
      setParams(next, { replace })
    },
    [setParams],
  )

  // The search box is local so typing stays responsive; it is debounced into the URL.
  const [draft, setDraft] = React.useState(committedSearch)
  // What we last wrote to the URL ourselves. The sync below must follow the URL only when
  // it changed from OUTSIDE (back button, a link, "Clear filters"); our own commit echoes
  // back as `committedSearch`, and treating that echo as external overwrites what the reader
  // typed inside the debounce window. The commit is trimmed, so pausing on "foo " alone
  // would rewrite the draft to "foo" and drop the just-typed space.
  const committedByUs = React.useRef(committedSearch)
  React.useEffect(() => {
    const fromOutside = committedSearch !== committedByUs.current
    committedByUs.current = committedSearch
    if (fromOutside) setDraft(committedSearch)
  }, [committedSearch])
  React.useEffect(() => {
    // A change that trims back to the committed search is not a search change. Committing it
    // would still run `update`, which resets the page — bouncing a reader who is on page 3 to
    // page 1 for a stray space.
    if (draft.trim() === committedSearch) return
    const timer = window.setTimeout(() => {
      const value = draft.trim()
      committedByUs.current = value
      // `replace`: a pause in typing is not a step worth a history entry.
      update(
        (next) => {
          if (value) next.set(PARAM.search, value)
          else next.delete(PARAM.search)
        },
        { replace: true },
      )
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
    }
    // Deliberately keyed on the draft only: re-arming when the URL changes would restart
    // the debounce on every unrelated filter click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  const actions = React.useMemo<TableUrlActions>(() => {
    const clearSchemaIds = (next: URLSearchParams) => {
      for (const def of schema) next.delete(def.id)
    }
    return {
      setSearch: setDraft,
      clearSearch: () => {
        setDraft("")
        // Mark the clear as ours, as the debounce's own commits do, so the sync above does
        // not read the URL losing `q` as something that happened outside — a Back press or
        // a link — and take it as the draft to restore.
        committedByUs.current = ""
        update((next) => {
          next.delete(PARAM.search)
        })
      },
      setPage: (nextPage, { replace = false } = {}) => {
        update(
          (next) => {
            if (nextPage > 1) next.set(PARAM.page, String(nextPage))
            else next.delete(PARAM.page)
          },
          { keepPage: true, replace },
        )
      },
      setPageSize: (size) => {
        update((next) => {
          if (size !== defaultPageSize && pageSizes.includes(size)) {
            next.set(PARAM.size, String(size))
          } else {
            next.delete(PARAM.size)
          }
        })
      },
      setSort: (nextSort) => {
        update((next) => {
          const isDefault =
            nextSort === null ||
            (nextSort.field === defaultSort.field && nextSort.dir === defaultSort.dir)
          if (isDefault) {
            next.delete(PARAM.sort)
            next.delete(PARAM.dir)
          } else {
            next.set(PARAM.sort, nextSort.field)
            next.set(PARAM.dir, nextSort.dir)
          }
        })
      },
      setFilters: (values) => {
        update((next) => {
          clearSchemaIds(next)
          for (const [id, value] of serializeFilters(values)) next.set(id, value)
        })
      },
      clearFilter: (id) => {
        update((next) => {
          next.delete(id)
        })
      },
      clearAllFilters: () => {
        // The Queue's Clear resets the whole query, search included (legacy); the admin
        // tables' clears the filter chips only, leaving the search box alone.
        if (clearSearchOnClearAll) setDraft("")
        update((next) => {
          clearSchemaIds(next)
          if (clearSearchOnClearAll) next.delete(PARAM.search)
        })
      },
    }
  }, [clearSearchOnClearAll, defaultPageSize, defaultSort, pageSizes, schema, update])

  return {
    state: {
      search: draft,
      committedSearch,
      page,
      pageSize,
      sort,
      filters,
      filterCount: activeFilterCount(filters),
      droppedFilterIds,
    },
    actions,
  }
}

function parsePositiveInt(raw: string | null): number | null {
  if (raw === null) return null
  const n = Number(raw)
  return Number.isInteger(n) && n >= 1 ? n : null
}

function parseSort(params: URLSearchParams, sortFields: readonly string[]): TableSort | null {
  const field = params.get(PARAM.sort)
  const dir = params.get(PARAM.dir)
  if (field === null || !sortFields.includes(field)) return null
  if (dir !== "asc" && dir !== "desc") return null
  return { field, dir }
}

/** Drop select values the schema does not offer, so a hand-edited URL cannot show a chip for
 *  an option that does not exist (or send it to the backend). A multi-select keeps the entries
 *  it knows and goes when none are left. Reports the ids it touched so the page can say so. */
function knownOptionsOnly(
  values: FilterValues,
  schema: FilterDef[],
): { values: FilterValues; dropped: string[] } {
  const out: FilterValues = {}
  const dropped: string[] = []
  for (const [id, value] of Object.entries(values)) {
    const def = schema.find((d) => d.id === id)
    if (def?.kind === "select") {
      const offered = (v: string) => def.options.some((o) => o.value === v)
      if (typeof value === "string" && !offered(value)) {
        dropped.push(id)
        continue
      }
      if (Array.isArray(value)) {
        const known = value.filter(offered)
        if (known.length < value.length) dropped.push(id)
        if (known.length > 0) out[id] = known
        continue
      }
    }
    out[id] = value
  }
  return { values: out, dropped }
}
